import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { isFullLengthClip, isClipInOriginalLanguage } from '@goldcast/player-config/src/utils/clipUtils';
import { updateClipDataWithSplits } from './clipUpdateDataUtilsWithSplits/updateWithSplits';
import { CLIP_UPDATE_TYPE } from './clipUpdateDataUtilsWithSplits/constants';
import RevertChange from './TimelinePlayerActionBar/RevertChanges';
import AddChapter from './TimelinePlayerActionBar/AddChapter';
import AddScene from './TimelinePlayerActionBar/AddScene';
import { AddMenuButton } from './TimelinePlayerActionBar/AddMenu';
import JumpBackward from './TimelinePlayerActionBar/JumpBackward';
import JumpForward from './TimelinePlayerActionBar/JumpForward';
import PlayPause from './TimelinePlayerActionBar/PlayPause';
import PlayerTime from './TimelinePlayerActionBar/PlayerTime';
import ZoomRange from './TimelinePlayerActionBar/ZoomRange';
import PlayerTimelineButton from './TimelinePlayerActionBar/PlayerTimelineButton';
import SpeakerTimeline from './SpeakerTimeline';
import { useZoomLevelCssVariable } from './hooks/useZoomLevelCssVariable';
import { useShortcuts } from './hooks/useShortcuts';
import DeleteSelected from './TimelinePlayerActionBar/DeleteSelected';
import RestoreSelected from './TimelinePlayerActionBar/RestoreSelected';
import CreateClipSelected from './TimelinePlayerActionBar/CreateClipSelected';
import { SplitButton } from './TimelinePlayerActionBar/SplitButton';
import { TIMELINE_COMPONENT_CLASS_IDENTIFIER } from './constants';
import useCreateClip from '../hooks/useCreateClip';
import { AudioControlsButton } from './TimelinePlayerActionBar/AudioControlsButton';
import PlaybackSpeedButton from './TimelinePlayerActionBar/PlaybackSpeedButton';
import ChaptersMenuButton from './ChaptersMenu/ChaptersMenuButton';
import { restrictToScrollContainerBounds, restrictVerticalDrift } from './dndUtils/dndRestricts';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { useRemotionPlayerPlaybackRate } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPlaybackRate';
import { isSplitsFeatureEnabled } from '@/stores/clip';

export const ClipNewTimeline = () => {
  const newTimelineContainerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const { seekTo } = useRemotionPlayerSeek();
  const { setPlaybackRate } = useRemotionPlayerPlaybackRate();

  const { clipData, clipId } = useClipsContext();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      updateClipDataWithSplits({
        clipId,
        actionType: CLIP_UPDATE_TYPE.REORDER_SEGMENTS,
        activeId: String(active.id),
        overId: String(over.id)
      });
    },
    [clipId]
  );

  const { isCreateClipAllowed } = useCreateClip({ clip: clipData });

  useEffect(() => {
    return () => {
      seekTo(0);
      setPlaybackRate(1);
    };
  }, [clipId, seekTo, setPlaybackRate]);

  const isChaptersEnabled = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsFeatureEnabled(featureFlags, clipData);
  }, [clipData, featureFlags]);

  const isAudioControlsEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Audio_Controls];
  }, [featureFlags]);

  const isPlaybackSpeedEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Playback_Speed];
  }, [featureFlags]);

  const isChaptersInMenuEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Chapters_In_Menu];
  }, [featureFlags]);

  const isRevertChangesAllowed = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  const isClipTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const isDeleteAllowed = useMemo(() => {
    return !isClipTranslated;
  }, [isClipTranslated]);

  const isRestoreAllowed = useMemo(() => {
    return !isClipTranslated;
  }, [isClipTranslated]);

  useZoomLevelCssVariable({ newTimelineContainerRef });
  useShortcuts();
  return (
    <>
      <div
        ref={newTimelineContainerRef}
        className={`mb-2 flex h-fit max-h-[40vh] w-full flex-col rounded-[10px] bg-white py-3 shadow-xl transition-[height] duration-300 ease-in-out ${TIMELINE_COMPONENT_CLASS_IDENTIFIER.FIXED_WIDTH_TIMELINE}`}
      >
        <div className="relative flex w-full items-center gap-2 px-3">
          <div className="flex items-center gap-2">
            {isRevertChangesAllowed && <RevertChange />}
            {featureFlags[FeatureFlagKeys.Use_CL_Timeline_Add_Menu] ? (
              <AddMenuButton />
            ) : (
              <>
                <AddScene />
                {isChaptersEnabled && <AddChapter />}
              </>
            )}

            {isSplitsEnabled && !isClipTranslated && <SplitButton />}
            <>
              {isCreateClipAllowed && <CreateClipSelected />}
              {isDeleteAllowed && <DeleteSelected />}
              {isRestoreAllowed && <RestoreSelected />}
            </>
            {isAudioControlsEnabled && <AudioControlsButton />}
          </div>
          <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2">
            {isChaptersEnabled && isChaptersInMenuEnabled && <ChaptersMenuButton />}
            <JumpBackward />
            <PlayPause />
            <JumpForward />
            <PlayerTime />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ZoomRange />
            {isPlaybackSpeedEnabled && <PlaybackSpeedButton />}
            <PlayerTimelineButton
              iconName={isExpanded ? 'IconLayoutBottombarCollapse' : 'IconLayoutBottombarExpand'}
              onClick={() => setIsExpanded(!isExpanded)}
              buttonId="expand-timeline"
              tooltipTitle={isExpanded ? 'Collapse' : 'Expand'}
            />
          </div>
        </div>
        {isExpanded && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictVerticalDrift, restrictToScrollContainerBounds]}
          >
            <SpeakerTimeline newTimelineContainerRef={newTimelineContainerRef} />
          </DndContext>
        )}
      </div>
    </>
  );
};
